import { FileText, Map, Copy, ScanLine, Camera, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    icon: FileText,
    title: "Impresión de Documentos",
    description: "Imprimimos todo tipo de archivos: documentos, diplomas, brochures, volantes, formularios y más. A color o blanco y negro, en tamaños carta, legal, A4 y otros formatos.",
    color: "bg-[#FFE4E8]",
    iconColor: "text-[#E65B78]",
    tags: ["Carta / Legal / A4", "B&N y Color", "Diplomas y Brochures", "Entrega Rápida"],
    large: true,
    image: "https://images.pexels.com/photos/11833899/pexels-photo-11833899.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    icon: Map,
    title: "Impresión de Planos",
    description: "Planos arquitectónicos e ingeniería en formatos grandes (A1, A0) a color o blanco y negro. Perfectos para proyectos de construcción y diseño. No incluye impresión en acero.",
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    tags: ["Formato A0/A1", "Color y B&N", "Alta Resolución"],
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80",
  },
  {
    icon: Copy,
    title: "Fotocopias",
    description: "Fotocopias rápidas y económicas de cualquier documento a color o blanco y negro. Trabajamos diplomas, contratos, formularios, cartas y todo tipo de archivos.",
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    tags: ["Color y B&N", "Todo tipo de docs", "Precio Accesible"],
    image: null,
  },
  {
    icon: ScanLine,
    title: "Escaneo Digital",
    description: "Digitalizamos tus documentos en alta resolución. Recibe tus archivos en PDF o imagen al instante.",
    color: "bg-green-50",
    iconColor: "text-green-600",
    tags: ["Alta Resolución", "PDF / JPG"],
    image: null,
  },
];

export default function ServicesSection() {
  return (
    <section id="servicios" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14 fade-in-up">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E65B78] mb-3">
            Lo que Hacemos
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#111111] mb-4">
            Nuestros Servicios
          </h2>
          <p className="text-base md:text-lg text-[#5C5C5C] max-w-xl mx-auto">
            Soluciones de impresión rápidas, económicas y de alta calidad para estudiantes y profesionales.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Large card */}
          <div
            data-testid="service-documentos"
            className="lg:col-span-2 rounded-3xl overflow-hidden card-hover relative group bg-[#FFE4E8] min-h-[300px] flex flex-col justify-end"
          >
            <img
              src={SERVICES[0].image}
              alt="Impresión de documentos"
              className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-300"
            />
            <div className="relative z-10 p-8">
              <div className="bg-[#E65B78] rounded-2xl p-3 w-fit mb-4">
                <FileText size={26} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-2">{SERVICES[0].title}</h3>
              <p className="text-[#5C5C5C] text-sm mb-4 max-w-md">{SERVICES[0].description}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {SERVICES[0].tags.map((tag) => (
                  <span key={tag} className="text-xs font-semibold bg-white/70 text-[#E65B78] border border-[#E65B78]/20 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <a
                href="https://wa.me/50589984547?text=Hola%2C%20necesito%20imprimir%20documentos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#E65B78] font-bold text-sm hover:gap-3 transition-all"
              >
                Solicitar ahora <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Planos */}
          <div
            data-testid="service-planos"
            className="rounded-3xl overflow-hidden card-hover relative group bg-blue-50 min-h-[300px] flex flex-col justify-end"
          >
            <img
              src={SERVICES[1].image}
              alt="Planos arquitectónicos"
              className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity"
            />
            <div className="relative z-10 p-7">
              <div className="bg-blue-600 rounded-2xl p-3 w-fit mb-4">
                <Map size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#111111] mb-2">{SERVICES[1].title}</h3>
              <p className="text-[#5C5C5C] text-sm mb-4">{SERVICES[1].description}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {SERVICES[1].tags.map((tag) => (
                  <span key={tag} className="text-xs font-semibold bg-white/70 text-blue-700 border border-blue-200 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <a
                href="https://wa.me/50589984547?text=Hola%2C%20necesito%20imprimir%20planos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-blue-600 font-bold text-sm hover:gap-3 transition-all"
              >
                Solicitar ahora <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Fotocopias */}
          <div data-testid="service-fotocopias" className="rounded-3xl bg-amber-50 p-7 card-hover">
            <div className="bg-amber-500 rounded-2xl p-3 w-fit mb-4">
              <Copy size={24} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#111111] mb-2">{SERVICES[2].title}</h3>
            <p className="text-[#5C5C5C] text-sm mb-4">{SERVICES[2].description}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {SERVICES[2].tags.map((tag) => (
                <span key={tag} className="text-xs font-semibold bg-white text-amber-700 border border-amber-200 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <a
              href="https://wa.me/50589984547?text=Hola%2C%20necesito%20hacer%20fotocopias"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-amber-600 font-bold text-sm hover:gap-3 transition-all"
            >
              Solicitar ahora <ArrowRight size={16} />
            </a>
          </div>

          {/* Escaneo */}
          <div data-testid="service-escaneo" className="rounded-3xl bg-green-50 p-7 card-hover">
            <div className="bg-green-600 rounded-2xl p-3 w-fit mb-4">
              <ScanLine size={24} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#111111] mb-2">{SERVICES[3].title}</h3>
            <p className="text-[#5C5C5C] text-sm mb-4">{SERVICES[3].description}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {SERVICES[3].tags.map((tag) => (
                <span key={tag} className="text-xs font-semibold bg-white text-green-700 border border-green-200 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <a
              href="https://wa.me/50589984547?text=Hola%2C%20necesito%20escanear%20documentos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-green-600 font-bold text-sm hover:gap-3 transition-all"
            >
              Solicitar ahora <ArrowRight size={16} />
            </a>
          </div>

          {/* Fotografía Consular */}
          <div data-testid="service-fotografia" className="lg:col-span-3 rounded-3xl bg-gradient-to-r from-[#FFE4E8] to-[#FFF0F3] p-7 card-hover border border-[#E65B78]/10">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0">
                <div className="bg-[#E65B78] rounded-2xl p-4 w-fit">
                  <Camera size={30} className="text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="text-2xl font-bold text-[#111111]">Fotografía Consular e Impresión</h3>
                  <span className="text-xs font-bold bg-[#E65B78] text-white px-3 py-1 rounded-full">Nuevo Servicio</span>
                </div>
                <p className="text-[#5C5C5C] text-sm leading-relaxed mb-4 max-w-2xl">
                  Tomamos y editamos tu foto con los requisitos oficiales para trámites consulares: visas, pasaportes, cédulas y más.
                  Imprimimos en el formato y tamaño exacto que necesitas. También puedes usar las fotos digitales para tus solicitudes en línea o a tu criterio personal.
                </p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {["Foto para Visa", "Foto para Pasaporte", "Foto para Cédula", "Edición Profesional", "Impresión Incluida", "Formato Digital"].map((tag) => (
                    <span key={tag} className="text-xs font-semibold bg-white text-[#E65B78] border border-[#E65B78]/20 px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="https://wa.me/50589984547?text=Hola%20Printacce%2C%20necesito%20fotos%20consulares%20y%20su%20impresi%C3%B3n"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-fotografia-btn"
                  className="inline-flex items-center gap-2 bg-[#E65B78] hover:bg-[#D44B66] text-white font-bold px-6 py-3 rounded-full transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Solicitar ahora <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
